import { Component, computed, effect, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BaseConfigService, Scheme } from '@base/services/config';
import { LocalStorageService } from 'app/core/localStorage/localStorage.service';

@Component({
  selector: 'theme-switcher',
  templateUrl: './theme-switcher.component.html',
  imports: [MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule],
})
export class ThemeSwitcherComponent {
  private readonly STORAGE_KEY = 'theme-scheme';

  schemes = [
    { id: 'light', name: 'Light', icon: 'heroicons_outline:sun' },
    { id: 'dark', name: 'Dark', icon: 'heroicons_outline:moon' },
    {
      id: 'auto',
      name: 'Auto',
      icon: 'heroicons_outline:computer-desktop',
    },
  ];

  // Signal for current scheme
  currentScheme = signal<Scheme>(this.getStoredScheme());

  // Computed properties
  currentSchemeName = computed(() => {
    return this.schemes.find((s) => s.id === this.currentScheme())?.name || 'Auto';
  });

  currentSchemeIcon = computed(() => {
    return this.schemes.find((s) => s.id === this.currentScheme())?.icon || 'heroicons_outline:computer-desktop';
  });

  constructor(
    private _configService: BaseConfigService,
    private _localStorageService: LocalStorageService,
  ) {
    // Effect to update Config when scheme changes
    effect(() => {
      const scheme = this.currentScheme();
      this._configService.config = { scheme };
      this._localStorageService.setItem(this.STORAGE_KEY, scheme);
    });

    // Initialize with stored scheme
    const storedScheme = this.getStoredScheme();
    this._configService.config = { scheme: storedScheme };
  }

  /**
   * Set the scheme
   */
  setScheme(scheme: Scheme): void {
    this.currentScheme.set(scheme);
  }

  /**
   * Get scheme from localStorage
   */
  private getStoredScheme(): Scheme {
    const stored = this._localStorageService.getItem(this.STORAGE_KEY);
    if (stored && ['light', 'dark', 'auto'].includes(stored)) {
      return stored as Scheme;
    }
    return 'auto'; // Default fallback
  }
}
